// src/admin/components/AdminKpiCards.jsx
//
// Top-level KPI cards: Total Camps, Total Patients, Total Billing,
// Company Cost, Profit, Profit Margin.
//
// Consumes the backend `summary` object exactly as returned — no
// financial values are computed here.

import React from 'react';
import { formatCurrency, formatPercentage, formatNumber } from './adminFormatters';
import { isNegativeProfit } from './adminHelpers';

function IconBuilding(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M6 21V5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v16M18 21V9a1 1 0 0 0-1-1h-3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h.01M9 11h.01M9 15h.01" />
    </svg>
  );
}

function IconUsers(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconWallet(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3M21 12h-4a2 2 0 0 0 0 4h4v-4z" />
    </svg>
  );
}

function IconReceipt(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 2h16v20l-3-2-3 2-3-2-3 2-3-2-1 2z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 8h8M8 12h8" />
    </svg>
  );
}

function IconTrendingUp(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M23 6l-9.5 9.5-5-5L1 18" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 6h6v6" />
    </svg>
  );
}

function IconPercent(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}

function KpiCard({ icon: Icon, label, value, accent, emphasize }) {
  return (
    <div
      className={`rounded-xl border bg-white p-4 shadow-sm transition-shadow hover:shadow-md ${
        emphasize ? 'border-[#11A8A4]/40' : 'border-[#E2E8E8]'
      }`}
    >
      <div
        className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${accent}1a`, color: accent }}
      >
        <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
      </div>
      <p className="text-xs font-medium uppercase tracking-wide text-[#6B7C7C]">{label}</p>
      <p className="mt-1 text-xl font-bold tabular-nums text-[#172B2B]">{value}</p>
    </div>
  );
}

export default function AdminKpiCards({ summary }) {
  const profit = summary?.total_profit;
  const negative = isNegativeProfit(profit);

  const cards = [
    {
      label: 'Total Camps',
      value: formatNumber(summary?.total_camps),
      icon: IconBuilding,
      accent: '#11A8A4',
    },
    {
      label: 'Total Patients',
      value: formatNumber(summary?.total_patients),
      icon: IconUsers,
      accent: '#11A8A4',
    },
    {
      label: 'Total Billing',
      value: formatCurrency(summary?.total_billing),
      icon: IconWallet,
      accent: '#11A8A4',
      emphasize: true,
    },
    {
      label: 'Company Cost',
      value: formatCurrency(summary?.total_company_cost),
      icon: IconReceipt,
      accent: '#D97706',
      emphasize: true,
    },
    {
      label: 'Ops Margin',
      value: formatCurrency(profit),
      icon: IconTrendingUp,
      accent: negative ? '#DC2626' : '#10B981',
      emphasize: true,
    },
    {
      label: 'Profit Margin',
      value: formatPercentage(summary?.margin_percentage),
      icon: IconPercent,
      accent: negative ? '#DC2626' : '#10B981',
      emphasize: true,
    },
  ];

  return (
    <div className="admin-kpi-grid grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map((c) => (
        <KpiCard key={c.label} {...c} />
      ))}
    </div>
  );
}
